import type { AdminStatsTrends, StatTrend } from "@/lib/admin-api-types";

export type DonationForTrends = {
  amount: number;
  status: "pending" | "paid" | "failed" | "refunded";
  paidAt: string;
  createdAt: string;
};

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) {
    return current > 0 ? null : 0;
  }
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

function trend(current: number, previous: number, label: string): StatTrend {
  const value = pctChange(current, previous);
  if (value === null) {
    return { value: null, label: "no prior period" };
  }
  return { value, label };
}

function sumPaidBetween(
  donations: DonationForTrends[],
  since: Date,
  until?: Date
): number {
  return donations
    .filter((d) => {
      if (d.status !== "paid") return false;
      const paidAt = new Date(d.paidAt);
      if (paidAt < since) return false;
      if (until && paidAt >= until) return false;
      return true;
    })
    .reduce((sum, d) => sum + d.amount, 0);
}

function countStatusBetween(
  donations: DonationForTrends[],
  status: DonationForTrends["status"],
  since?: Date,
  until?: Date,
  dateField: "paidAt" | "createdAt" = "createdAt"
): number {
  return donations.filter((d) => {
    if (d.status !== status) return false;
    const when = new Date(d[dateField]);
    if (since && when < since) return false;
    if (until && when >= until) return false;
    return true;
  }).length;
}

function computeAdminStatsFromDonations(donations: DonationForTrends[]): {
  donationCount: number;
  pendingCount: number;
  failedCount: number;
  trends: AdminStatsTrends;
} {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfDay);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfLastMonth =
    startOfMonth.getMonth() === 0
      ? new Date(startOfMonth.getFullYear() - 1, 11, 1)
      : new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() - 1, 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const startOfLastYear = new Date(now.getFullYear() - 1, 0, 1);
  const sameDayLastYear = new Date(
    now.getFullYear() - 1,
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59
  );
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
  const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const twoDaysAgo = new Date(now.getTime() - 48 * 24 * 60 * 60 * 1000);

  const raisedToday = sumPaidBetween(donations, startOfDay);
  const raisedYesterday = sumPaidBetween(
    donations,
    startOfYesterday,
    startOfDay
  );
  const raisedMonth = sumPaidBetween(donations, startOfMonth);
  // Same elapsed window last month (MTD vs prior MTD), not the full prior month.
  const elapsedMs = now.getTime() - startOfMonth.getTime();
  const samePeriodLastMonthEnd = new Date(
    Math.min(startOfLastMonth.getTime() + elapsedMs, startOfMonth.getTime())
  );
  const raisedLastMonth = sumPaidBetween(
    donations,
    startOfLastMonth,
    samePeriodLastMonthEnd
  );
  const raisedYear = sumPaidBetween(donations, startOfYear);
  const raisedLastYearYtd = sumPaidBetween(
    donations,
    startOfLastYear,
    sameDayLastYear
  );

  const paidLastWeek = countStatusBetween(
    donations,
    "paid",
    weekAgo,
    now,
    "paidAt"
  );
  const paidPrevWeek = countStatusBetween(
    donations,
    "paid",
    twoWeeksAgo,
    weekAgo,
    "paidAt"
  );

  const pendingLastDay = countStatusBetween(
    donations,
    "pending",
    dayAgo,
    now
  );
  const pendingPrevDay = countStatusBetween(
    donations,
    "pending",
    twoDaysAgo,
    dayAgo
  );

  const failedLastWeek = countStatusBetween(
    donations,
    "failed",
    weekAgo,
    now
  );
  const failedPrevWeek = countStatusBetween(
    donations,
    "failed",
    twoWeeksAgo,
    weekAgo
  );

  return {
    donationCount: paidLastWeek,
    pendingCount: pendingLastDay,
    failedCount: failedLastWeek,
    trends: {
      raisedToday: trend(raisedToday, raisedYesterday, "vs yesterday"),
      raisedMonth: trend(raisedMonth, raisedLastMonth, "vs last month"),
      raisedYear: trend(raisedYear, raisedLastYearYtd, "vs last year"),
      donationCount: trend(paidLastWeek, paidPrevWeek, "vs last week"),
      pendingCount: trend(pendingLastDay, pendingPrevDay, "vs yesterday"),
      failedCount: trend(failedLastWeek, failedPrevWeek, "vs last week"),
    },
  };
}

export function computeAdminStatsTrends(
  donations: DonationForTrends[]
): AdminStatsTrends {
  return computeAdminStatsFromDonations(donations).trends;
}

export function computeAdminStatsWindowCounts(donations: DonationForTrends[]): {
  donationCount: number;
  pendingCount: number;
  failedCount: number;
} {
  const { donationCount, pendingCount, failedCount } =
    computeAdminStatsFromDonations(donations);
  return { donationCount, pendingCount, failedCount };
}
